/* iter55f94 — Voice Coach drawer.
 *
 * Two tabs:
 *   • Prose — audits narrative voice against the project's declared
 *     voice (from Story Bible / genre). Shows overall + per-chapter
 *     scores and flagged passages.
 *   • Character — pick a character; audits their dialogue for voice
 *     consistency vs. traits + description. Shows flagged lines.
 *
 * Both tabs have a "Fix all in one click" button that turns every
 * flagged rewrite into a pending tracked-change suggestion (source
 * "voice-coach"). Suggestions surface inline in the manuscript via
 * the existing SuggestionsDrawer + highlight pipeline, so writers can
 * accept/reject each one where they read.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Mic, User, X, Wand2, Check, Ban, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const color = s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-amber-700' : 'text-red-700';
  return <span className={`font-mono-stat ${color}`}>{s}/100</span>;
}

export default function VoiceCoachDrawer({ open, onClose, projectId, deepLink }) {
  const [tab, setTab] = useState('prose');
  const [characters, setCharacters] = useState([]);
  const [selectedCid, setSelectedCid] = useState('');
  // Per-tab state.
  const [prose, setProse] = useState({ loading: false, result: null, applying: false, applied: null });
  const [charState, setCharState] = useState({ loading: false, result: null, applying: false, applied: null });
  const [bulk, setBulk] = useState(null); // 'accept'|'decline'|null

  // Async job polling ref so we can cancel on unmount / re-run
  const proseJobRef = React.useRef(null);

  const runProse = async () => {
    if (!projectId) {
      toast.error('No project loaded — please open a project and try again.');
      return;
    }
    // Cancel any in-flight poll
    proseJobRef.current = null;
    setProse((s) => ({ ...s, loading: true, result: null, applied: null }));
    try {
      // 1. Start async job — returns immediately with a job_id
      const start = await axios.post(
        `${API}/projects/${projectId}/voice-coach/prose/start`,
        {}, { withCredentials: true, timeout: 15000 },
      );
      const jobId  = start.data?.job_id;
      const token  = Symbol(); // unique per run, used to detect stale polls
      proseJobRef.current = token;

      // 2. Poll until done or error
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3500));
        if (proseJobRef.current !== token) return; // component unmounted or re-run
        let st;
        try {
          const r = await axios.get(
            `${API}/projects/${projectId}/voice-coach/prose/status/${jobId}`,
            { withCredentials: true, timeout: 10000 },
          );
          st = r.data;
        } catch { break; }
        if (st.status === 'done') {
          setProse({ loading: false, result: { project_id: projectId, ...st.result }, applying: false, applied: null });
          done = true;
        } else if (st.status === 'error') {
          throw new Error(st.error || 'Audit failed.');
        }
      }
    } catch (e) {
      setProse((s) => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || e?.message || 'Prose voice audit failed.');
    }
  };

  // Deep-link: auto-run prose audit when drawer opens with a deep-link hint
  useEffect(() => {
    if (!open || !deepLink || !projectId) return;
    if (deepLink.tab === 'character' && deepLink.character_id) {
      setTab('character');
      setSelectedCid(deepLink.character_id);
    } else {
      setTab('prose');
      setTimeout(() => {
        setProse((s) => (s.result || s.loading) ? s : { ...s, loading: true, result: null, applied: null });
        runProse();
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deepLink?.at, projectId]);

  // Cleanup on unmount
  useEffect(() => () => { proseJobRef.current = null; }, []);
  // Load character list when drawer opens.
  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/characters`,
          { withCredentials: true, timeout: 15000 });
        const list = r.data?.characters || r.data || [];
        setCharacters(list);
        if (list.length && !selectedCid) setSelectedCid(list[0].id);
      } catch { /* silent — the tab will show an empty state */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const applyProse = async () => {
    setProse((s) => ({ ...s, applying: true }));
    try {
      const r = await axios.post(`${API}/projects/${projectId}/voice-coach/prose/apply`,
        {}, { withCredentials: true, timeout: 120000 });
      setProse((s) => ({ ...s, applying: false, applied: r.data }));
      toast.success(r.data.created_count
        ? `Queued ${r.data.created_count} in-voice rewrite${r.data.created_count === 1 ? '' : 's'}.`
        : 'No rewrites queued — prose is already on-voice.');
    } catch (e) {
      setProse((s) => ({ ...s, applying: false }));
      toast.error(e?.response?.data?.detail || 'Could not queue the rewrites.');
    }
  };

  const runChar = async () => {
    if (!projectId) {
      toast.error('No project loaded — please open a project and try again.');
      return;
    }
    if (!selectedCid) { toast.error('Pick a character first.'); return; }
    setCharState((s) => ({ ...s, loading: true, result: null, applied: null }));
    try {
      const r = await axios.post(`${API}/projects/${projectId}/voice-coach/character/${selectedCid}`,
        {}, { withCredentials: true, timeout: 120000 });
      setCharState((s) => ({ ...s, loading: false, result: r.data }));
    } catch (e) {
      setCharState((s) => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || 'Character voice audit failed.');
    }
  };
  const applyChar = async () => {
    setCharState((s) => ({ ...s, applying: true }));
    try {
      const r = await axios.post(`${API}/projects/${projectId}/voice-coach/character/${selectedCid}/apply`,
        {}, { withCredentials: true, timeout: 120000 });
      setCharState((s) => ({ ...s, applying: false, applied: r.data }));
      toast.success(r.data.created_count
        ? `Queued ${r.data.created_count} in-voice rewrite${r.data.created_count === 1 ? '' : 's'} for ${r.data.character_name}.`
        : `${r.data.character_name}'s dialogue is already in-voice.`);
    } catch (e) {
      setCharState((s) => ({ ...s, applying: false }));
      toast.error(e?.response?.data?.detail || 'Could not queue the rewrites.');
    }
  };

  const bulkResolve = (applied, tabKey) => async (action) => {
    const ids = (applied?.created || []).map((c) => c.suggestion_id);
    if (!ids.length) return;
    setBulk(`${tabKey}-${action}`);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/suggestions/bulk-resolve`,
        { action, ids }, { withCredentials: true, timeout: 60000 });
      if (action === 'accept') {
        toast.success(`Applied ${r.data.applied}/${r.data.accepted} rewrites.`);
      } else {
        toast.success(`Declined ${r.data.declined} rewrites.`);
      }
      if (tabKey === 'prose') setProse((s) => ({ ...s, applied: null }));
      else                    setCharState((s) => ({ ...s, applied: null }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed.');
    } finally { setBulk(null); }
  };

  const selectedChar = useMemo(
    () => characters.find((c) => c.id === selectedCid) || null,
    [characters, selectedCid],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-panel-border bg-card shadow-xl"
      data-testid="voice-coach-drawer"
    >
      <header className="flex items-center justify-between border-b border-panel-border px-3 py-2">
        <div className="flex items-center gap-2 text-[13.5px] font-semibold uppercase tracking-wider">
          <Mic className="h-3.5 w-3.5 text-primary" /> Voice coach
        </div>
        <button onClick={onClose} className="rounded-sm p-1 hover:bg-secondary" data-testid="voice-coach-close">
          <X className="h-3.5 w-3.5" />
        </button>
      </header>

      {/* Tab switcher */}
      <div className="flex shrink-0 border-b border-panel-border bg-secondary/30">
        <button
          onClick={() => setTab('prose')}
          data-testid="voice-coach-tab-prose"
          className={`flex-1 py-1.5 text-[13.5px] font-semibold ${tab === 'prose' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
        >
          Prose voice
        </button>
        <button
          onClick={() => setTab('character')}
          data-testid="voice-coach-tab-character"
          className={`flex-1 py-1.5 text-[13.5px] font-semibold ${tab === 'character' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
        >
          Character voice
        </button>
      </div>

      <div className="scroll-thin flex-1 overflow-y-auto p-4">
        {tab === 'prose' && (
          <ProseTab
            projectId={projectId}
            state={prose}
            onRun={runProse}
            onApply={applyProse}
            onBulk={bulkResolve(prose.applied, 'prose')}
            bulk={bulk}
            onClose={onClose}
          />
        )}
        {tab === 'character' && (
          <CharacterTab
            characters={characters}
            selectedCid={selectedCid}
            setSelectedCid={setSelectedCid}
            selectedChar={selectedChar}
            state={charState}
            onRun={runChar}
            onApply={applyChar}
            onBulk={bulkResolve(charState.applied, 'char')}
            bulk={bulk}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function AppliedSummary({ applied, onBulk, bulk, tabKey, onClose }) {
  if (!applied) return null;
  const inFlight = (act) => bulk === `${tabKey}-${act}`;
  return (
    <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50/60 p-3"
         data-testid={`voice-coach-${tabKey}-summary`}>
      <div className="flex items-center gap-2 text-[13.5px] font-semibold text-emerald-900">
        <Check className="h-3.5 w-3.5" />
        {applied.created_count} in-voice rewrite{applied.created_count === 1 ? '' : 's'} queued
      </div>
      {applied.created_count > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => onBulk('accept')}
            disabled={bulk !== null}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[13px] font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            data-testid={`voice-coach-${tabKey}-accept-all`}
          >
            {inFlight('accept') ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Accept all
          </button>
          <button
            onClick={() => onBulk('decline')}
            disabled={bulk !== null}
            className="inline-flex items-center gap-1.5 rounded-md border border-panel-border bg-white px-2.5 py-1.5 text-[13px] font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
            data-testid={`voice-coach-${tabKey}-reject-all`}
          >
            {inFlight('decline') ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
            Reject all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
            data-testid={`voice-coach-${tabKey}-open-editor`}
          >
            Open in editor <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function ProseTab({ projectId, state, onRun, onApply, onBulk, bulk, onClose }) {
  const { loading, result, applying, applied } = state;
  return (
    <div className="space-y-4">
      {!result && !loading && (
        <button
          onClick={onRun}
          disabled={!projectId}
          className="mx-auto flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
          data-testid="voice-coach-prose-run"
        >
          <Mic className="h-3.5 w-3.5" /> Audit prose voice
        </button>
      )}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-8 text-[13.5px] text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span>Auditing every chapter against the declared voice…</span>
          <span className="text-[13px] opacity-70">This runs in the background — results appear here when ready</span>
        </div>
      )}
      {result && (
        <>
          <div className="rounded-md border border-panel-border bg-secondary/40 p-3" data-testid="voice-coach-prose-overall">
            <div className="flex items-center justify-between">
              <span className="text-[13px] uppercase tracking-wider text-muted-foreground">Overall</span>
              <ScoreBadge score={result.overall?.score} />
            </div>
            <div className="mt-1 text-[13px] text-muted-foreground">
              Declared voice: <span className="font-semibold text-foreground">{result.declared_voice}</span>
              {result.overall?.detected_voice && result.overall.detected_voice !== result.declared_voice && (
                <> · Detected: <span className="font-semibold text-foreground">{result.overall.detected_voice}</span></>
              )}
            </div>
            <p className="mt-2 text-[13.5px] leading-relaxed">{result.overall?.summary || ''}</p>
          </div>

          {!applied && (result.chapters || []).some((c) => (c.flags || []).length) && (
            <button
              onClick={onApply}
              disabled={applying}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
              data-testid="voice-coach-prose-apply-all"
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              Fix all voice drift as tracked changes
            </button>
          )}

          <AppliedSummary applied={applied} onBulk={onBulk} bulk={bulk} tabKey="prose" onClose={onClose} />

          <div className="space-y-2">
            {(result.chapters || []).map((ch) => (
              <div
                key={ch.number}
                data-testid={`voice-coach-prose-ch-${ch.number}`}
                className="rounded-md border border-panel-border bg-background p-3"
              >
                <div className="flex items-center justify-between text-[13.5px]">
                  <span className="font-semibold">Ch {ch.number}</span>
                  <ScoreBadge score={ch.score} />
                </div>
                {ch.drift && (
                  <div className="mt-1 text-[13px] italic text-muted-foreground">{ch.drift}</div>
                )}
                {(ch.flags || []).map((f, i) => (
                  <div key={i} className="mt-2 rounded-sm bg-amber-50 p-2 text-[13.5px] leading-relaxed">
                    <div className="line-through decoration-red-500/60 opacity-80">&ldquo;{f.snippet}&rdquo;</div>
                    <div className="mt-1 text-emerald-900">{f.rewrite}</div>
                    {f.why && <div className="mt-1 text-[13px] italic text-muted-foreground">{f.why}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <button
            onClick={onRun}
            className="w-full rounded-md border border-panel-border py-1.5 text-[13.5px] hover:bg-secondary"
            data-testid="voice-coach-prose-rerun"
          >
            Re-run audit
          </button>
        </>
      )}
    </div>
  );
}

function CharacterTab({ characters, selectedCid, setSelectedCid, selectedChar,
                       state, onRun, onApply, onBulk, bulk, onClose }) {
  const { loading, result, applying, applied } = state;
  if (!characters.length) {
    return (
      <div className="rounded-md border border-dashed border-panel-border p-6 text-center text-[13.5px] text-muted-foreground">
        No characters yet. Add characters in the Character Bible to run a voice audit.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
          Character
        </label>
        <select
          value={selectedCid}
          onChange={(e) => setSelectedCid(e.target.value)}
          data-testid="voice-coach-char-select"
          className="mt-1 w-full rounded-sm border border-panel-border bg-background px-2 py-1.5 text-[13.5px]"
        >
          {characters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || '(unnamed)'}{c.role ? ` · ${c.role}` : ''}
            </option>
          ))}
        </select>
        {selectedChar && (
          <div className="mt-1 text-[13px] text-muted-foreground">
            {selectedChar.voice ? `Voice: ${selectedChar.voice}` : 'No voice note — add one in the Bible for sharper audits.'}
          </div>
        )}
      </div>

      {!result && !loading && (
        <button
          onClick={onRun}
          className="flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
          data-testid="voice-coach-char-run"
        >
          <User className="h-3.5 w-3.5" /> Audit character voice
        </button>
      )}
      {loading && (
        <div className="flex flex-col items-center gap-2 py-8 text-[13.5px] text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Reading every chapter for {selectedChar?.name || 'this character'}&rsquo;s dialogue…
        </div>
      )}
      {result && (
        <>
          <div className="rounded-md border border-panel-border bg-secondary/40 p-3" data-testid="voice-coach-char-overall">
            <div className="flex items-center justify-between">
              <span className="text-[13px] uppercase tracking-wider text-muted-foreground">
                {result.character_name}
              </span>
              <ScoreBadge score={result.overall?.score} />
            </div>
            <p className="mt-2 text-[13.5px] leading-relaxed">{result.overall?.summary || ''}</p>
          </div>

          {!applied && (result.flags || []).length > 0 && (
            <button
              onClick={onApply}
              disabled={applying}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
              data-testid="voice-coach-char-apply-all"
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              Fix all as tracked changes
            </button>
          )}

          <AppliedSummary applied={applied} onBulk={onBulk} bulk={bulk} tabKey="char" onClose={onClose} />

          <div className="space-y-2">
            {(result.flags || []).length === 0 && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50/60 p-3 text-[13.5px] text-emerald-900">
                Every flagged line is already in-voice. 🎉
              </div>
            )}
            {(result.flags || []).map((f, i) => (
              <div key={i} className="rounded-md border border-panel-border bg-background p-3">
                <div className="text-[13px] uppercase tracking-wider text-muted-foreground">
                  Ch {f.chapter_number}
                </div>
                <div className="mt-1 rounded-sm bg-red-50 p-2 text-[13.5px] italic leading-relaxed line-through decoration-red-500/60 opacity-80">
                  {f.line}
                </div>
                <div className="mt-1 rounded-sm bg-emerald-50 p-2 text-[13.5px] leading-relaxed text-emerald-900">
                  {f.rewrite}
                </div>
                {f.why && <div className="mt-1 text-[13px] italic text-muted-foreground">{f.why}</div>}
              </div>
            ))}
          </div>
          <button
            onClick={onRun}
            className="w-full rounded-md border border-panel-border py-1.5 text-[13.5px] hover:bg-secondary"
            data-testid="voice-coach-char-rerun"
          >
            Re-run audit
          </button>
        </>
      )}
    </div>
  );
}
